// @file Composable to enable dropping files/links/text to create a post.
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getAttachmentsFromDraft, MAX_PHOTO_ALBUM_ATTACHMENTS } from '@@/bits/post_drafts'
import { useComposerModalAlertStore } from '@@/pinia/composer_modal_alert'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceDragAndDropEventsStore } from '@@/pinia/surface_drag_and_drop_events_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { isInnermostDropTarget } from '@@/pragmatic-drag-and-drop/helpers'
import type { DraftPost } from '@@/types'
import { useCanHandleDropFromExternal } from '@@/vuecomposables/useDragAndDropFromExternalMonitor'
import { usePhotoAlbumEditor } from '@@/vuecomposables/usePhotoAlbumEditor'
import { dropTargetForExternal, type ExternalEventPayloadMap } from '@atlaskit/pragmatic-drag-and-drop/external/adapter'
import { containsFiles, getFiles } from '@atlaskit/pragmatic-drag-and-drop/external/file'
import { containsText, getText } from '@atlaskit/pragmatic-drag-and-drop/external/text'
import { containsURLs, getURLs } from '@atlaskit/pragmatic-drag-and-drop/external/url'
import type { CleanupFn } from '@atlaskit/pragmatic-drag-and-drop/types'
import { isEmpty } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

interface PostPosition {
  top: number
  left: number
}

enum DraggedDataType {
  File,
  Link,
  Text,
}

/**
 * Handles drop events to create a post.
 */
const useDropToCreatePost = ({
  postComposerModalElement,
  onFileDropWithExistingAttachment,
}: {
  /**
   * By default, this composable handles drop events on the whole document.
   * If you pass the post composer modal element here, the dropped content
   * will be added to that composer.
   */
  postComposerModalElement?: Ref<Element | undefined>
  /**
   * Optional callback executed *before* showing the default alert
   * when a file is dropped while the active draft already has an attachment.
   * If this function is provided and returns `false`, the default alert and
   * replacement logic will be skipped. If it returns `true` or is not provided,
   * the default logic proceeds.
   * @param file The file that was dropped.
   * @returns {boolean} Whether to proceed with the default alert/logic.
   */
  onFileDropWithExistingAttachment?: (file: File) => boolean
} = {}): void => {
  const { stopHandlingDragEvent, setDropActionMessage } = useSurfaceDragAndDropEventsStore()
  const { openComposerAlert } = useComposerModalAlertStore()

  const { xEditThumbnailModal } = storeToRefs(usePostComposerModalStore())

  const { createPostImmediately } = useSurfacePostsStore()

  const surfaceDraftsStore = useSurfaceDraftsStore()
  const {
    startNewDraft,
    updateActiveDraft,
    uploadFileActiveDraft,
    removeDraftAttachment,
    replaceActiveDraftUploadingFile,
    replaceActiveDraftAttachmentWithLink,
  } = surfaceDraftsStore
  const { activeDraft, activeDraftUploadingFile, showPublishAllDraftsPopover, isAnyDraftActive } =
    storeToRefs(surfaceDraftsStore)
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  /*****************
   * DRAGGING STATES
   *****************/

  const isDroppingIntoPostComposer = computed(() => postComposerModalElement?.value != null)
  const draggedDataType = ref<DraggedDataType | null>(null)

  const setDraggedDataType = (e: ExternalEventPayloadMap['onDragStart']): void => {
    if (containsFiles(e)) {
      draggedDataType.value = DraggedDataType.File
    } else if (containsURLs(e)) {
      draggedDataType.value = DraggedDataType.Link
    } else if (containsText(e)) {
      draggedDataType.value = DraggedDataType.Text
    }
  }

  const dropActionMessage = computed<string | null>(() => {
    if (draggedDataType.value === DraggedDataType.File) {
      if (isDroppingIntoPostComposer.value) return __('Drop the file here to add it to this post')
      return isAnyDraftActive.value
        ? __('Drop the file anywhere to add it to this post')
        : __('Drop files anywhere to create a new post')
    }
    if (draggedDataType.value === DraggedDataType.Link) {
      if (isDroppingIntoPostComposer.value) return __('Drop the link here to add it to this post')
      return isAnyDraftActive.value
        ? __('Drop the link anywhere to add it to this post')
        : __('Drop links anywhere to create a new post')
    }
    if (draggedDataType.value === DraggedDataType.Text) {
      if (isDroppingIntoPostComposer.value) return __('Drop the text here to add it to this post')
      return isAnyDraftActive.value
        ? __('Drop the text anywhere to add it to this post')
        : __('Drop text anywhere to create a new post')
    }
    return null
  })

  /***************
   * DROP HANDLERS
   ***************/
  const { onUploadStart } = usePhotoAlbumEditor()
  const activeDraftHasAttachmentOrUploadingFile = computed(
    () => !isEmpty(activeDraft.value?.attachment) || activeDraftUploadingFile.value != null,
  )
  const activeDraftHasPhotoAlbumAttachment = computed(() => {
    const attachments = getAttachmentsFromDraft(activeDraft.value as DraftPost)
    return attachments.length > 1
  })
  const activeDraftHasSingleImageAttachment = computed(() => {
    return (
      !activeDraftHasPhotoAlbumAttachment.value &&
      activeDraft.value?.attachment != null &&
      surfaceAttachmentsStore.getLinkAttributesForUrl(activeDraft.value?.attachment)?.content_category === 'photo'
    )
  })

  /**
   * Returns post top and left position (the post appears at the drop position in Canvas).
   */
  const getDropPosition = (e: ExternalEventPayloadMap['onDrop']): PostPosition => {
    const { pageX, pageY } = e.location.current.input
    const wishListElement = document.getElementById('wish-list')
    const left = Math.max(pageX - (wishListElement != null ? wishListElement.getBoundingClientRect().left : 0), 0)
    const top = Math.max(pageY - (wishListElement != null ? wishListElement.getBoundingClientRect().top : 0), 0)
    return { top, left }
  }

  // #region File drop handlers

  /**
   * Handles dropping multiple files when an active draft is present.
   * Shows an alert informing the user that only one file can be added at a time.
   * @param files The array of files dropped.
   */
  const handleMultipleFileDropIfActiveDraftIsPresent = (files: File[]): void => {
    openComposerAlert({
      title: __('Multiple files added'),
      body: __('While writing a post only one file can be added at a time'),
      primaryActionText: __('Okay'),
      primaryActions: [],
    })
  }

  /**
   * Handles dropping a single file when an active draft is present.
   *
   * Photo Album handling:
   * - If photo album feature is enabled:
   *   - Checks photo album attachment limit and shows error if exceeded
   *   - Fetches link attributes for existing draft attachment if needed
   *   - For existing photo albums or single image posts:
   *     - If dropped file is an image: adds it to the photo album
   *     - If dropped file is not an image and there's a photo album: shows unsupported file type alert
   *
   * Regular file handling:
   * - If the draft already has an attachment or an upload is in progress,
   *   it prompts the user to confirm replacement (respecting the optional `onFileDropWithExistingAttachment` callback).
   * - Otherwise, it uploads the file to the active draft.
   *
   * @param fileToUpload The single file that was dropped.
   */
  const handleSingleFileDropIfActiveDraftIsPresent = async (fileToUpload: File): Promise<void> => {
    const draft = activeDraft.value as DraftPost
    if (isAppUsing('photoAlbum')) {
      if (
        activeDraftHasPhotoAlbumAttachment.value &&
        getAttachmentsFromDraft(draft).length === MAX_PHOTO_ALBUM_ATTACHMENTS
      ) {
        globalSnackbarStore.setSnackbar({
          message: __('No more than %{count} images can be added to a post at a time', {
            count: MAX_PHOTO_ALBUM_ATTACHMENTS,
          }),
          notificationType: SnackbarNotificationType.error,
        })
        return
      }

      if (draft.attachment != null && surfaceAttachmentsStore.getLinkAttributesForUrl(draft.attachment) == null) {
        await surfaceAttachmentsStore.fetchAndStoreLinkAttributes(draft.attachment)
      }

      if (
        (activeDraftHasSingleImageAttachment.value || activeDraftHasPhotoAlbumAttachment.value) &&
        fileToUpload.type.startsWith('image/')
      ) {
        onUploadStart({ file: fileToUpload })
        return
      } else if (activeDraftHasPhotoAlbumAttachment.value && !fileToUpload.type.startsWith('image/')) {
        openComposerAlert({
          title: __('Unsupported file type'),
          body: __('Only images can be added to the photo album'),
          primaryActionText: __('Okay'),
          primaryActions: [],
        })
        return
      }
    }

    // Ask if user wants to replace the current attachment.
    if (activeDraftHasAttachmentOrUploadingFile.value) {
      // Check if a custom handler is provided and if it signals to stop
      let proceed = true
      if (onFileDropWithExistingAttachment != null) {
        proceed = onFileDropWithExistingAttachment(fileToUpload)
      }
      if (!proceed) return

      // Only proceed with the default alert if the callback didn't return false
      const primaryActions =
        activeDraft.value?.attachment != null
          ? [
              // An attachment already exists
              // -> remove and upload the new one.
              () => {
                if (activeDraft.value?.cid != null) removeDraftAttachment(activeDraft.value.cid)
              },
              () => uploadFileActiveDraft({ file: fileToUpload }),
            ]
          : [
              // An attachment is being uploaded,
              // when the user click "Replace", it can either be still uploading or already uploaded and added to activeDraft.attachment.
              () => replaceActiveDraftUploadingFile({ file: fileToUpload }),
            ]
      openComposerAlert({
        title: __('An attachment already exists'),
        body: __('Are you sure you want to replace your current attachment?'),
        primaryActionText: __('Replace'),
        secondaryActionText: __('Cancel'),
        secondaryActions: [],
        primaryActions,
      })
      return
    }

    uploadFileActiveDraft({ file: fileToUpload })
  }

  /**
   * Handles dropping a single file when no draft is active.
   * Creates a new draft with the dropped file as an attachment at the drop position.
   * @param file The single file dropped.
   * @param position The position where the file was dropped.
   */
  const handleSingleFileDropIfNoActiveDraft = (file: File, position: PostPosition): void => {
    const attributes = { file, ...position }
    const activeDraftCid = activeDraft.value?.cid
    const shouldStartEditing = activeDraftCid == null
    startNewDraft({ attributes, shouldStartEditing })
  }

  /**
   * Handles dropping multiple files when no draft is active.
   * Creates minimized posts immediately for each dropped file, slightly offset from each other,
   * and shows the "Publish All Drafts" popover.
   * @param files The array of files dropped.
   * @param position The position where the files were dropped.
   */
  const handleMultipleFileDropIfNoActiveDraft = (files: File[], position: PostPosition): void => {
    files.forEach((file, index) => {
      const newPost = {
        attributes: {
          file,
          // Place each post a few pixels away from each other.
          top: position.top + index * 16,
          left: position.left + index * 16,
        },
      }
      createPostImmediately(newPost)
    })
    showPublishAllDraftsPopover.value = true
  }

  /**
   * Main handler for file drop events.
   * Determines if a draft is active and the number of files dropped,
   * then delegates to the appropriate specialized handler function.
   * @param files The array of files dropped.
   * @param position The position where the files were dropped.
   */
  const handleFileDrop = ({ files, position }: { files: File[]; position: PostPosition }): void => {
    if (files.length === 0) return

    if (isAnyDraftActive.value) {
      if (files.length === 1) {
        void handleSingleFileDropIfActiveDraftIsPresent(files[0])
      } else {
        handleMultipleFileDropIfActiveDraftIsPresent(files)
      }
    } else {
      // No active draft, create new posts
      if (files.length === 1) {
        handleSingleFileDropIfNoActiveDraft(files[0], position)
      } else {
        handleMultipleFileDropIfNoActiveDraft(files, position)
      }
    }
  }
  // #endregion

  const handleLinkDrop = ({ urls, position }: { urls: string[]; position: PostPosition }): void => {
    if (urls.length === 0) return

    if (isAnyDraftActive.value) {
      // For links, we conveniently pick the first one to add as an attachment to the active draft.
      const link = urls[0]

      // Ask if user wants to replace the current attachment.
      if (activeDraftHasAttachmentOrUploadingFile.value) {
        openComposerAlert({
          title: __('An attachment already exists'),
          body: __('Are you sure you want to replace your current attachment?'),
          primaryActionText: __('Replace'),
          secondaryActionText: __('Cancel'),
          secondaryActions: [],
          primaryActions: [() => replaceActiveDraftAttachmentWithLink({ link })],
        })
        return
      }

      updateActiveDraft({
        attachment: link,
        wish_content: {
          attachment_props: {
            url: link,
          },
          is_processed: false,
        },
      })
      return
    }

    if (urls.length === 1) {
      // There's only 1 link, create a new draft and add it inside.
      const attachment = urls[0]
      const attributes = { attachment, ...position }
      const activeDraftCid = activeDraft.value?.cid
      const shouldStartEditing = activeDraftCid == null
      startNewDraft({ attributes, shouldStartEditing })
    } else {
      // Add the links in minimized drafts.
      urls.forEach((attachment, index) => {
        const newPost = {
          attributes: {
            attachment,
            // Place each post a few pixels away from each other.
            top: position.top + index * 16,
            left: position.left + index * 16,
          },
        }
        createPostImmediately(newPost)
      })
    }
  }

  const handleTextDrop = ({ text, position }: { text: string | null; position: PostPosition }): void => {
    if (text == null) return

    if (isAnyDraftActive.value) {
      // Append the text to the draft body.
      updateActiveDraft({
        body: (activeDraft.value?.body ?? '') + '<br/>' + text,
      })
      return
    }

    const attributes = { body: text, ...position }
    const activeDraftCid = activeDraft.value?.cid
    const shouldStartEditing = activeDraftCid == null
    startNewDraft({ attributes, shouldStartEditing })
  }

  /************
   * LIFE CYCLE
   ************/

  let cleanUp: CleanupFn

  const { canHandle } = useCanHandleDropFromExternal()
  const { canIPost } = storeToRefs(useSurfacePermissionsStore())
  const { shouldHandleDragAndDropToPost } = storeToRefs(useSurfacePostPropertiesStore())
  const { xExpandedPostPanel } = storeToRefs(useExpandedPostStore())

  onMounted(() => {
    const element = isDroppingIntoPostComposer.value ? postComposerModalElement?.value : document.body
    if (element == null) return
    cleanUp = dropTargetForExternal({
      element,
      canDrop: (e) => {
        return (
          !xEditThumbnailModal.value && // If edit thumbnail modal is open skip handling drop for other cases
          canHandle(e) &&
          canIPost.value && // User must have permission.
          shouldHandleDragAndDropToPost.value && // Attachments must be enabled.
          !xExpandedPostPanel.value // Disable in expanded view.
        )
      },
      onDragEnter: (e) => {
        setDraggedDataType(e)
        setDropActionMessage(dropActionMessage.value)
      },
      onDropTargetChange: () => {
        setDropActionMessage(dropActionMessage.value)
      },
      onDragLeave: () => {
        // When handling drop events in the post composer modal,
        // don't stop if the drag leaves the composer as we can
        // handle it to create a new post instead of adding to
        // the active composer.
        if (!isDroppingIntoPostComposer.value) {
          stopHandlingDragEvent()
          setDropActionMessage(null)
        }
      },
      onDrop: (e) => {
        // We have 2 drop targets in play:
        // 1. The active post composer modal (for adding to the active draft)
        // 2. `document.body` (for creating a new draft)
        // When we drop something into the composer, both `onDrop` of the 2 targets are executed.
        // (documentation: https://atlassian.design/components/pragmatic-drag-and-drop/core-package/drop-targets#nested-drop-targets)
        // We only want to handle the innermost drop target to prevent double handling.
        // We do this by checking if the target element is the same as the listened element.
        if (!isInnermostDropTarget(e)) {
          return
        }

        const position = getDropPosition(e)

        if (draggedDataType.value === DraggedDataType.File) {
          handleFileDrop({
            files: getFiles(e),
            position,
          })
        } else if (draggedDataType.value === DraggedDataType.Link) {
          handleLinkDrop({
            urls: getURLs(e),
            position,
          })
        } else if (draggedDataType.value === DraggedDataType.Text) {
          handleTextDrop({
            text: getText(e),
            position,
          })
        }
      },
    })
  })

  onBeforeUnmount(() => {
    cleanUp?.()
  })
}

export { useDropToCreatePost }
